import State from 'store/State';
import {
    selectActiveInput,
    selectPortfolioSelection,
} from 'portfolio/store/selectors';
import {
    ContactSupportRequestState,
    PortfolioInfo,
} from 'communication/types/ContactSupportRequest';
import platform from 'platform';
import { getGlobalWindowVariables } from 'utils/globalWindowVariables';
import { PortfolioService } from 'portfolio/PortfolioService';
import { AppState } from 'app/store/AppState';
import omit from 'lodash/omit';
import { WidgetInstancesState } from 'widgets/store/WidgetsState';
import {
    BlotterWidgetsState,
    BlotterWidgetState,
} from 'widgets/blotter/store/BlotterWidgetState';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import cloneDeep from 'lodash/cloneDeep';
import { getWindowLocation } from 'utils/components/location';
import { selectIsFeatureEnabled } from 'app/store/featureToggles/selectors';
import { WellKnownFeatureToggleKeys } from 'app/store/featureToggles/FeatureTogglesState';
import IndigoApi from 'communication/IndigoApi';
import { selectAllOrders } from 'app/store/orders/selectors';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { LogMessagesStoreService } from 'logging/store/LogMessagesStoreService';
import IndigoServiceClient from 'communication/IndigoServiceClient';
import { EmbeddedApiService } from 'embedded/api/EmbeddedApiService';
import {
    EmbeddedApiState,
    initialEmbeddedApiState,
} from 'embedded/api/store/EmbeddedApiState';
import { bootstrapInTopics } from 'embedded/api/in/bootstrapper';
import { isSanitisesPayload } from 'embedded/api/in/InMessageTopic';
import { InTopic } from 'embedded/api/in/InTopic';
import { bootstrapOutTopics } from 'embedded/api/out/bootstrapper';
import { OutTopic } from 'embedded/api/out/OutTopic';
import { WidgetStreamingService } from 'widgets/streaming/WidgetStreamingService';
import { MemoryMonitoringService } from 'performance/MemoryMonitoringService';
import { selectBloombergState } from 'app/glue/bloomberg/store/bloombergSlice';
import { DateTime } from 'luxon';
import { StreamingServiceInfo } from 'communication/streaming/StreamingService';
import { selectBuildVersion } from 'app/store/general/selectors';

export function mapStateToSupport(
    state: State,
    name: string | undefined,
    screenshot: string | undefined,
    portfolioService: PortfolioService,
    logMessagesStoreService: LogMessagesStoreService,
    indigoApi: IndigoApi,
    indigoServiceClient: IndigoServiceClient,
    embeddedApiService: EmbeddedApiService,
    widgetStreamingService: WidgetStreamingService,
    memoryMonitoringService: MemoryMonitoringService
): ContactSupportRequestState {
    const stateForSupportCall: ContactSupportRequestState = {
        timeStamp: new Date(),
        name,
        screenshot,
        activeInput: selectActiveInput(state),
        jSessionId: indigoServiceClient.jSessionId ?? 'Not Set!',
        dimensions: {
            height: window.innerHeight,
            width: window.innerWidth,
        },
        indigoVersion: {
            buildVersion: getGlobalWindowVariables().buildVersion ?? '-',
            serverBuildVersion: selectBuildVersion(state) ?? '-',
        },
        location: { ...getWindowLocation() },
        globalVariables: getGlobalWindowVariables(),
        measuredMemory: memoryMonitoringService.results,
        platform,
        portfolio: mapPortfolio(state, portfolioService),
        state: getSmallerState(state),
        streams: sanitiseStreamInfo(indigoApi.getStreamInfo()),
        widgetStreamManagers: widgetStreamingService.getInfo(),
        logMessages: logMessagesStoreService.logs,
        embeddedApiSubscribers: embeddedApiService.getSubscribersInfo(),
        bloombergMessages: selectBloombergState(state),
        timeZone: {
            name: DateTime.local().zoneName ?? '',
            offset: DateTime.local().offset,
            offsetName: DateTime.local().offsetNameLong ?? '',
            isInDst: DateTime.local().isInDST,
        },
    };

    const cloned = cloneDeep(stateForSupportCall);

    const includeSessionIds = selectIsFeatureEnabled(
        state,
        WellKnownFeatureToggleKeys.SupportRequestIncludeSessionIds
    );
    if (!includeSessionIds) {
        removeSessions(cloned);
    }

    return cloned as ContactSupportRequestState;
}

function getSmallerState(state: State): State {
    return {
        ...state,
        app: getSmallerAppState(state.app),
        widgets: {
            ...state.widgets,
            instances: getSmallerWidgetInstancesState(state.widgets.instances),
        },
        embeddedApi: state.embeddedApi
            ? getSmallerEmbeddedApi(state.embeddedApi)
            : initialEmbeddedApiState,
    };
}

function getSmallerAppState(
    appState: AppState
): AppState & { shareUsersLength?: number } {
    return {
        ...appState,
        orders: omit(appState.orders, ['data']),
        shareUsers: [],
        shareUsersLength: appState.shareUsers?.length,
    };
}

function getSmallerWidgetInstancesState(
    widgetsState: WidgetInstancesState
): WidgetInstancesState {
    return {
        ...widgetsState,
        blotter: getSmallerBlotterWidgetsState(widgetsState.blotter),
    };
}

function getSmallerBlotterWidgetsState(
    blotterWidgetsState: BlotterWidgetsState
): BlotterWidgetsState {
    const res: BlotterWidgetsState = {};
    for (const key of Object.keys(blotterWidgetsState)) {
        const blotterState = blotterWidgetsState[key];
        const smallerBlotterState = omit(blotterState, ['filteredOrders']);
        res[key] = smallerBlotterState as BlotterWidgetState;
    }
    return res;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function removeSessions(target: any) {
    try {
        for (const key of Object.keys(target)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const value: any = target[key];

            if (key.toLowerCase() === 'globalvariables' && value.key) {
                value.key = '****';
            }

            if (key.toLowerCase() === 'session' && value.id) {
                value.id = '****';
            }
            if (key.toLowerCase() === 'location') {
                hashLocation(value);
            }

            if (
                key.toLowerCase() === 'sessionid' ||
                key.toLowerCase() === 'uid'
            ) {
                target[key] = '****';
            }

            if (key.toLowerCase() === 'authorization') {
                target[key] = '****';
            }

            if (isObject(value)) {
                removeSessions(value);
            }

            if (isArray(value)) {
                for (const item of value) {
                    removeSessions(item);
                }
            }
        }
    } catch (_err) {
        return target;
    }
}

function hashLocation(value: Location) {
    const hashLoc = (v: string) => {
        const split = v.split(';');
        if (split.length > 1) {
            return split[0] + ';****';
        }
        return v;
    };

    if (value.href) {
        value.href = hashLoc(value.href);
    }
    if (value.pathname) {
        value.pathname = hashLoc(value.pathname);
    }
    if (value.search) {
        value.search = '?****';
    }
}

function mapPortfolio(
    state: State,
    portfolioService: PortfolioService
): PortfolioInfo {
    const selection = new PortfolioSelectionWrapper(
        selectPortfolioSelection(state)
    );
    const allOrders = selectAllOrders(state);

    const filtered = portfolioService.getFilteredPortfolio();

    const ret: PortfolioInfo = {
        ordersCount: allOrders.length,
        filteredPortfolioInfoCount: filtered.length,
        first100Orders: allOrders.slice(0, 100),
        first100FilteredOrders: filtered.slice(0, 100),
        selection: {
            selectedOrdersCount: selection.selectedOrders.length,
            selectedPositionsCount: selection.selectedPositions.length,
            selectedSymbolQuantitiesCount:
                selection.selectedSymbolQuantities.length,
            selectedSymbolsCount: selection.selectedSymbols.length,
            symbology: selection.symbology,
        },
    };

    ret.selection.selectedOrders = selection.selectedOrders.slice(0, 100);
    ret.selection.selectedPositions = selection.selectedPositions.slice(0, 100);
    ret.selection.selectedSymbolQuantities =
        selection.selectedSymbolQuantities.slice(0, 200);
    ret.selection.selectedSymbols = selection.selectedSymbols.slice(0, 200);

    return ret;
}

function getSmallerEmbeddedApi(
    embeddedApi: EmbeddedApiState
): EmbeddedApiState {
    const inTopics = bootstrapInTopics();

    const ret: EmbeddedApiState = cloneDeep(embeddedApi);

    if (ret.receivedMessages) {
        for (const inTopicName of Object.keys(ret.receivedMessages)) {
            const inTopic = inTopics[inTopicName as InTopic];
            if (inTopic && isSanitisesPayload(inTopic)) {
                for (const message of ret.receivedMessages[inTopicName]) {
                    message.payload = inTopic.sanitizePayload(message.payload);
                }
            }
        }
    }

    if (ret.sentMessages) {
        const outTopics = bootstrapOutTopics();
        for (const outTopicName of Object.keys(ret.sentMessages)) {
            const outTopic = outTopics[outTopicName as OutTopic];
            if (outTopic && isSanitisesPayload(outTopic)) {
                for (const message of ret.sentMessages[outTopicName]) {
                    message.message = outTopic.sanitizePayload(message.message);
                }
            }
        }
    }

    return ret;
}

function sanitiseStreamInfo(info: StreamingServiceInfo | undefined) {
    if (!info) {
        return undefined;
    }

    const ret = cloneDeep(info);
    makeArraysSmallerVisitor(ret);
    return ret;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function makeArraysSmallerVisitor(target: any) {
    if (!target) {
        return;
    }
    for (const prop of Object.keys(target)) {
        if (Array.isArray(target[prop])) {
            if (target[prop].length > 100) {
                target[prop + 'Length'] = target[prop].length;
                target[prop] = [];
            }
            for (const item of target[prop]) {
                makeArraysSmallerVisitor(item);
            }
        } else {
            if (typeof target[prop] === 'object' && target[prop] !== null) {
                makeArraysSmallerVisitor(target[prop]);
            }
        }
    }
}
